import { useMutation, useQueryClient } from "@tanstack/react-query";
import { contentService, type PublishBulkResult } from "@/features/content/services/contentService";
import { handleApiError, toastMutationSuccess } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type { ContentEntry, CreatePageRequest, RejectContentRequest } from "@/types/content";

type ContentRef = { contentType: string; id: string };

export function useApproveContent() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ContentRef>({
    mutationFn: ({ contentType, id }) => contentService.approve(contentType, id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, { fallbackMessage: "تمت الموافقة على المحتوى" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useSubmitContent() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ContentRef>({
    mutationFn: ({ contentType, id }) => contentService.submit(contentType, id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, { fallbackMessage: "تم إرسال المحتوى للمراجعة" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function usePublishContent() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ContentRef>({
    mutationFn: ({ contentType, id }) => contentService.publish(contentType, id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, { fallbackMessage: "تم نشر المحتوى" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useAssignDepartmentBulk() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { contentType: string; ids: string[]; departmentId: string | null }
  >({
    mutationFn: ({ contentType, ids, departmentId }) =>
      contentService.assignDepartmentBulk(contentType, ids, departmentId),
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, {
        fallbackMessage: `تم تعيين ${vars.ids.length} صفحة`,
      });
    },
    onError: (error) => handleApiError(error),
  });
}

export function usePublishBulk() {
  const queryClient = useQueryClient();
  return useMutation<PublishBulkResult, Error, { contentType: string; ids: string[] }>({
    mutationFn: ({ contentType, ids }) =>
      contentService.publishBulk(contentType, ids),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      const fallbackMessage =
        data.failed > 0
          ? `تم نشر ${data.count} صفحة، وتخطّي ${data.failed}`
          : `تم نشر ${data.count} صفحة`;
      toastMutationSuccess(data, { fallbackMessage });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useRollbackContentVersion() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    ContentRef & { versionId: string }
  >({
    mutationFn: ({ contentType, id, versionId }) =>
      contentService.rollbackVersion(contentType, id, versionId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, { fallbackMessage: "تمت استعادة النسخة" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useSetPageDepartment() {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    ContentRef & { departmentId: string | null }
  >({
    mutationFn: ({ contentType, id, departmentId }) =>
      contentService.setDepartment(contentType, id, departmentId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, { fallbackMessage: "تم تحديث دائرة الصفحة" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useRejectContent() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ContentRef & { payload?: RejectContentRequest }>({
    mutationFn: ({ contentType, id, payload }) =>
      contentService.reject(contentType, id, payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, { fallbackMessage: "تم رفض المحتوى" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useCreatePage() {
  const queryClient = useQueryClient();
  return useMutation<ContentEntry, Error, { contentType: string; data: CreatePageRequest }>({
    mutationFn: ({ contentType, data }) => contentService.create(contentType, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, { fallbackMessage: "تم إنشاء الصفحة" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useDeleteContent() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, ContentRef>({
    mutationFn: ({ contentType, id }) => contentService.delete(contentType, id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      toastMutationSuccess(res, { fallbackMessage: "تم حذف الصفحة" });
    },
    onError: (error) => handleApiError(error),
  });
}
